import React from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../components/layout/Shell';
import {
  ShieldCheck, Lock, Database, ShieldStar, Key, ArrowsClockwise,
} from '@phosphor-icons/react';

const Section = ({ title, eyebrow, children, testId }) => (
  <section className="mb-12" data-testid={testId}>
    {eyebrow && <div className="label-eyebrow text-primary mb-2">{eyebrow}</div>}
    <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tighter leading-tight mb-4">
      {title}
    </h2>
    <div className="prose prose-neutral max-w-none text-sm lg:text-base leading-relaxed text-foreground space-y-3">
      {children}
    </div>
  </section>
);

const Security = () => {
  return (
    <Shell>
      <div className="max-w-[820px] mx-auto px-6 py-16" data-testid="security-page">
        <div className="mb-12">
          <div className="label-eyebrow text-primary mb-3 flex items-center gap-2">
            <Lock size={14} weight="fill" /> SECURITY
          </div>
          <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-4">
            How we keep your data safe.
          </h1>
          <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
            No hand-waving. Here's exactly what's in place today and what we're working on next.
          </p>
        </div>

        <Section title="Transport security" eyebrow="1 · IN MOTION" testId="security-transport">
          <ul className="list-disc pl-5 space-y-1">
            <li>All traffic is TLS 1.3 only. HTTP/2 over Cloudflare.</li>
            <li>Wildcard certificate from Google Trust Services, auto-renewed.</li>
            <li>HSTS enforced. Cookies set <code>Secure; HttpOnly; SameSite=Lax</code>.</li>
            <li>Every API response carries hardened headers — <code>X-Content-Type-Options: nosniff</code>, <code>X-Frame-Options: SAMEORIGIN</code>, strict <code>Referrer-Policy</code>, and a tight <code>Permissions-Policy</code>.</li>
          </ul>
        </Section>

        <Section title="Storage security" eyebrow="2 · AT REST" testId="security-storage">
          <ul className="list-disc pl-5 space-y-1">
            <li>Database: MongoDB Atlas, encrypted at rest with AES-256, network-isolated VPC, IP allowlisted to our app servers only.</li>
            <li>Uploaded files (résumés, work samples, thumbnails) live in cloud object storage with server-side encryption.</li>
            <li>Passwords are hashed with bcrypt (cost factor 12). We never see and never store your plaintext password.</li>
            <li>Auth tokens are JWTs signed with a 256-bit secret, rotated on a schedule, 7-day expiry.</li>
            <li>Backups: encrypted daily snapshots, 30-day rolling retention.</li>
          </ul>
        </Section>

        <Section title="Access control" eyebrow="3 · INSIDE THE BUILDING" testId="security-access">
          <ul className="list-disc pl-5 space-y-1">
            <li>Production access is restricted to two named engineers, both with hardware-key 2FA.</li>
            <li>Every admin action is logged with timestamp + actor + target. We can audit on request.</li>
            <li>No engineer can browse candidate or employer data in the normal course of work. Database access is read-only for debugging, write access is alert-emitting.</li>
            <li>We rotate access reviews quarterly.</li>
          </ul>
        </Section>

        <Section title="Anti-gaming & abuse" eyebrow="4 · INTEGRITY" testId="security-abuse">
          <ul className="list-disc pl-5 space-y-1">
            <li>Every assessment attempt is analysed for AI-generated writing patterns (Claude flags suspicious essays).</li>
            <li>Cross-attempt duplicate detection — copy-pasting answers between candidates is detected and flagged on the employer's Why panel.</li>
            <li>Brute-force login protection: progressive cooldowns after 5 wrong attempts.</li>
            <li>Rate limiting on scoring endpoints to prevent score-farming.</li>
          </ul>
        </Section>

        <Section title="Vendor security" eyebrow="5 · SUB-PROCESSORS" testId="security-vendors">
          <p>We use a tight set of audited vendors. Each has a DPA on file:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>MongoDB Atlas</strong> — primary database (SOC 2 Type II, ISO 27001)</li>
            <li><strong>Cloudflare</strong> — DDoS protection, WAF, TLS termination (SOC 2 Type II, ISO 27001)</li>
            <li><strong>Anthropic (Claude API)</strong> — LLM scoring (does not train on our traffic per their commercial terms)</li>
            <li><strong>Resend</strong> — transactional email delivery</li>
            <li><strong>Stripe</strong> — billing (PCI-DSS Level 1 — we never touch card data)</li>
            <li><strong>PostHog</strong> — product analytics with anonymised identifiers</li>
          </ul>
        </Section>

        <Section title="Incident response" eyebrow="6 · WHEN THINGS GO WRONG" testId="security-incident">
          <ul className="list-disc pl-5 space-y-1">
            <li>24-hour acknowledgement target for any reported security issue.</li>
            <li>72-hour breach-notification target to affected users (faster where law requires).</li>
            <li>Post-mortem published to all affected accounts within 14 days.</li>
          </ul>
          <p>
            Found a vulnerability? Email{' '}
            <a href="mailto:security@hikarihiring.com" className="text-primary underline">
              security@hikarihiring.com
            </a>. We acknowledge within 24 hours. We're working on a formal bug-bounty program — for now, responsible disclosure is gratefully received and credited.
          </p>
        </Section>

        <Section title="What we're working on" eyebrow="7 · ROADMAP" testId="security-roadmap">
          <ul className="list-disc pl-5 space-y-1">
            <li>SOC 2 Type I audit (in progress — target Q3 2026)</li>
            <li>SSO for employers (SAML 2.0)</li>
            <li>Granular data-retention controls per role family</li>
            <li>Region-pinned data residency for EU and UK customers</li>
          </ul>
        </Section>

        <Section title="Reasonable assurances" eyebrow="8 · HONESTY" testId="security-honesty">
          <p>
            We are not yet SOC 2 certified, ISO 27001 certified, or HIPAA compliant. We
            run security with the same rigour you'd expect from a serious early-stage SaaS company —
            but we're not pretending to have certifications we don't have. If your hiring use case
            requires any of those today, please talk to us and we'll be honest about whether we're
            the right partner yet.
          </p>
        </Section>

        {/* Footer nav */}
        <div className="border-t border-border pt-8 flex items-center justify-between flex-wrap gap-4">
          <Link to="/" className="label-eyebrow text-muted-foreground hover:text-foreground">
            ← Back to home
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/privacy" className="label-eyebrow text-muted-foreground hover:text-foreground">
              Privacy →
            </Link>
            <Link to="/methodology" className="label-eyebrow text-muted-foreground hover:text-foreground">
              Methodology →
            </Link>
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default Security;
